import httpRequestHandler from '../utils/httpRequestHandler';
import CACHE_TIME from 'src/constants/cacheTime';

// Module-level Tier-2 cache, mirroring insightQoreService. Only genuinely
// static lookups are cached here — run status and run history are deliberately
// excluded because they change while the caller is watching them.
const _actuarialServiceCache = new Map();

const _acGet = (key) => {
    const e = _actuarialServiceCache.get(key);
    if (!e) return undefined;
    if (Date.now() > e.expiresAt) {
        _actuarialServiceCache.delete(key);
        return undefined;
    }
    return e.value;
};

const _acSet = (key, value, ttl) => {
    _actuarialServiceCache.set(key, { value, expiresAt: Date.now() + ttl });
};

/**
 * Clears the ActuarialService module-level cache. Wired into the logout cache
 * sync alongside the other service caches so entries cannot leak across user
 * sessions in the same browser tab.
 *
 * @param {string} [methodName] Optional method name to clear scoped entries.
 *                              When omitted, every cached entry is removed.
 */
export function clearActuarialServiceCache(methodName) {
    if (methodName) {
        for (const key of _actuarialServiceCache.keys()) {
            if (key.startsWith(`${methodName}:`)) {
                _actuarialServiceCache.delete(key);
            }
        }
        return;
    }
    _actuarialServiceCache.clear();
}

export const PERIOD_KIND = {
    QUARTER: 'Quarter',
    SEMESTER: 'Semester',
    EXPLICIT: 'Explicit',
};

export const RUN_STATUS = {
    QUEUED: 'Queued',
    RUNNING: 'Running',
    SUCCEEDED: 'Succeeded',
    FAILED: 'Failed',
    CANCELLED: 'Cancelled',
};

export const isTerminalStatus = (status) =>
    status === RUN_STATUS.SUCCEEDED ||
    status === RUN_STATUS.FAILED ||
    status === RUN_STATUS.CANCELLED;

class ActuarialService {
    #getCacheKey = (methodName, ...args) => `${methodName}:${JSON.stringify(args)}`;

    #getCachedOrFetch = async (cacheKey, fetchFn, ttl) => {
        const cached = _acGet(cacheKey);
        if (cached !== undefined) return cached;

        const result = await fetchFn();
        _acSet(cacheKey, result, ttl);
        return result;
    };

    /**
     * Export groups the caller may run. Sourced from the API rather than the
     * client so a product-mix change never requires a frontend release.
     */
    getExportGroups = async () => {
        const cacheKey = this.#getCacheKey('getExportGroups');
        return this.#getCachedOrFetch(
            cacheKey,
            async () => {
                const { data } = await httpRequestHandler.get('/raw-data-export/groups');
                return data?.groups ?? [];
            },
            CACHE_TIME.THIRTY_MIN,
        );
    };

    /**
     * Enqueues an export. Resolves with the accepted run (202) — the workbook
     * is built asynchronously, so the caller must poll getRun for completion.
     */
    startRun = async ({ period, group, start, end }) => {
        const payload = { period, group };
        if (period === PERIOD_KIND.EXPLICIT) {
            payload.start = start;
            payload.end = end;
        }

        const { data } = await httpRequestHandler.post('/raw-data-export/run', payload);
        return data;
    };

    /** Never cached: this is the value the UI is polling for a change in. */
    getRun = async (runId) => {
        const { data } = await httpRequestHandler.get(`/raw-data-export/run/${encodeURIComponent(runId)}`);
        return data;
    };

    /** Never cached: new runs must appear without waiting out a TTL. */
    getRunHistory = async () => {
        const { data } = await httpRequestHandler.get('/raw-data-export/runs');
        return data?.runs ?? [];
    };

    /**
     * Asks the API where the finished workbook lives.
     *
     * Returns a short-lived SAS URL pointing at blob storage, which the caller
     * navigates to directly. The file is far too large to pull through this
     * authenticated client as a blob - the API cannot buffer it into a response
     * body, and the browser would hold the whole workbook in memory.
     */
    downloadWorkbook = async (runId) => {
        const { data } = await httpRequestHandler.get(
            `/raw-data-export/run/${encodeURIComponent(runId)}/download`,
        );
        return { url: data?.url, fileName: data?.fileName, bytes: data?.bytes };
    };

    cancelRun = async (runId) => {
        const { data } = await httpRequestHandler.post(
            `/raw-data-export/run/${encodeURIComponent(runId)}/cancel`,
        );
        return data;
    };
}

const actuarialService = new ActuarialService();

export default actuarialService;
